import type {
  Badge,
  Comment,
  CrewFeedItem,
  FollowingOut,
  FollowRequestOut,
  PersonalBests,
  PublicProfileOut,
  PublicSessionOut,
  PublicUserOut,
  SentRequestOut,
  StatsBreakdown,
  VolumeEntry,
} from "@/types/api";
import client from "./client";

export async function searchUsers(q: string): Promise<PublicUserOut[]> {
  const res = await client.get<PublicUserOut[]>("/crew/search", { params: { q } });
  return res.data;
}

export async function sendFollowRequest(handle: string): Promise<void> {
  await client.post(`/crew/${handle}/request`);
}

export async function cancelFollowRequest(handle: string): Promise<void> {
  await client.delete(`/crew/${handle}/request`);
}

export async function unfollow(handle: string): Promise<void> {
  await client.delete(`/crew/${handle}/follow`);
}

export async function getIncomingRequests(): Promise<FollowRequestOut[]> {
  const res = await client.get<FollowRequestOut[]>("/crew/requests/incoming");
  return res.data;
}

export async function acceptRequest(requesterHandle: string, dataSharingLevel: string = "minimal"): Promise<void> {
  await client.post(`/crew/requests/${requesterHandle}/accept`, null, { params: { data_sharing: dataSharingLevel } });
}

export async function updateSharingLevel(followerHandle: string, level: string): Promise<void> {
  await client.patch(`/crew/${followerHandle}/sharing`, { data_sharing: level });
}

export async function denyRequest(requesterHandle: string): Promise<void> {
  await client.post(`/crew/requests/${requesterHandle}/deny`);
}

export async function listFollowing(): Promise<FollowingOut[]> {
  const res = await client.get<FollowingOut[]>("/crew/following");
  return res.data;
}

export async function listFollowers(): Promise<FollowingOut[]> {
  const res = await client.get<FollowingOut[]>("/crew/followers");
  return res.data;
}

export async function getSentRequests(): Promise<SentRequestOut[]> {
  const res = await client.get<SentRequestOut[]>("/crew/requests/sent");
  return res.data;
}

export async function removeFollower(handle: string): Promise<void> {
  await client.delete(`/crew/${handle}/follower`);
}

export async function getProfile(handle: string): Promise<PublicProfileOut> {
  const res = await client.get<PublicProfileOut>(`/crew/${handle}/profile`);
  return res.data;
}

export async function getUserBadges(handle: string): Promise<Badge[]> {
  const res = await client.get<Badge[]>(`/crew/${handle}/badges`);
  return res.data;
}

export async function getCrewSessions(handle: string): Promise<PublicSessionOut[]> {
  const res = await client.get<PublicSessionOut[]>(`/crew/${handle}/sessions`);
  return res.data;
}

export async function getCrewFeed(): Promise<CrewFeedItem[]> {
  const res = await client.get<CrewFeedItem[]>("/crew/feed");
  return res.data;
}

export async function getCrewBests(handle: string, since?: string): Promise<PersonalBests> {
  const res = await client.get<PersonalBests>(`/crew/${handle}/stats/bests`, { params: since ? { since } : undefined });
  return res.data;
}

export async function getCrewVolume(handle: string): Promise<VolumeEntry[]> {
  const res = await client.get<VolumeEntry[]>(`/crew/${handle}/stats/volume`);
  return res.data;
}

export async function getCrewBreakdown(handle: string, since?: string): Promise<StatsBreakdown> {
  const res = await client.get<StatsBreakdown>(`/crew/${handle}/stats/breakdown`, { params: since ? { since } : undefined });
  return res.data;
}

export async function reactToSession(handle: string, sessionId: number, emoji: string): Promise<void> {
  await client.post(`/crew/${handle}/sessions/${sessionId}/react`, null, { params: { emoji } });
}

export async function removeReaction(handle: string, sessionId: number): Promise<void> {
  await client.delete(`/crew/${handle}/sessions/${sessionId}/react`);
}

export async function getComments(handle: string, sessionId: number): Promise<Comment[]> {
  const res = await client.get<Comment[]>(`/crew/${handle}/sessions/${sessionId}/comments`);
  return res.data;
}

export async function addComment(handle: string, sessionId: number, body: string): Promise<Comment> {
  const res = await client.post<Comment>(`/crew/${handle}/sessions/${sessionId}/comments`, { body });
  return res.data;
}

export async function deleteComment(handle: string, sessionId: number, commentId: number): Promise<void> {
  await client.delete(`/crew/${handle}/sessions/${sessionId}/comments/${commentId}`);
}

export async function markCommentsViewed(handle: string, sessionId: number): Promise<void> {
  await client.post(`/crew/${handle}/sessions/${sessionId}/comments/view`);
}
